import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MapPin,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import Swal from 'sweetalert2';
import CityModal from '../components/CityModal';
import { cityApi } from '../services/api';

const CityManagement = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [error, setError] = useState('');

  // Load cities from API
  const loadCities = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await cityApi.getCities();
      setCities(response || []);
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Failed to load cities. Please try again.');
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadCities();
  }, []);

  // Apply frontend filtering for display only
  const filteredCities = cities.filter(city => {
    // Only apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = city.cityName?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Only apply status filter if there's a status selected
    if (filterStatus && filterStatus !== '') {
      if (city.isActive !== (filterStatus === 'active')) return false;
    }
    
    return true;
  });

  // Handle edit city
  const handleEditCity = (city) => {
    setEditingCity(city);
    setShowModal(true);
  };

  // Handle save city (create or update)
  const handleSaveCity = async (cityData) => {
    try {
      if (editingCity) {
        // Update existing city
        await cityApi.updateCity(editingCity.cityID, cityData);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'City updated successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        // Create new city
        await cityApi.createCity(cityData);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'City created successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      }
      
      // Reload cities and close modal
      await loadCities();
      setShowModal(false);
      setEditingCity(null);
    } catch (err) {
      console.error('Error saving city:', err);
      
      // Handle specific error messages
      let errorMessage = 'Failed to save city. Please try again.';
      
      if (err.message) {
        if (err.message.includes('already exists')) {
          errorMessage = `City name already exists. Please choose a different name.`;
        } else {
          errorMessage = err.message;
        }
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMessage,
        confirmButtonText: 'OK'
      });
    }
  };

  // Handle toggle city status
  const handleToggleStatus = async (cityId) => {
    // Find the city to get current status
    const city = cities.find(c => c.cityID === cityId);
    if (!city) return;

    const isCurrentlyActive = city.isActive;
    const action = isCurrentlyActive ? 'inactive' : 'active';
    const actionText = isCurrentlyActive ? 'deactivate' : 'activate';

    const result = await Swal.fire({
      icon: 'warning',
      title: `Mark as ${action.charAt(0).toUpperCase() + action.slice(1)}?`,
      text: `Are you sure you want to ${actionText} this city?`,
      showCancelButton: true,
      confirmButtonColor: isCurrentlyActive ? '#f59e0b' : '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${actionText} it!`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await cityApi.toggleStatus(cityId);
        await loadCities();
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `City has been marked as ${action} successfully.`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error toggling city status:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: err.message || 'Failed to update city status. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // Handle delete city
  const handleDeleteCity = async (cityId) => {
    const city = cities.find(c => c.cityID === cityId);
    if (!city) return;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: `This will permanently delete the city "${city.cityName}". This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await cityApi.deleteCity(cityId);
        await loadCities();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'City has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error deleting city:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: err.message || 'Failed to delete city. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading cities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">City Management</h1>
              <p className="text-gray-600">Manage cities for your transport system</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add City
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by city name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Filter Results */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredCities.length} of {cities.length} cities
            {filteredCities.length > 8 && (
              <span className="ml-2 text-blue-600">• Scroll to see more</span>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Cities Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-96 overflow-y-auto relative">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCities.map((city) => (
                  <tr key={city.cityID} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <MapPin className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{city.cityName}</p>
                          <p className="text-sm text-gray-500">ID: {city.cityID}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        city.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {city.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm text-gray-900">
                        {new Date(city.createdDate).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditCity(city)}
                          className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit City"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(city.cityID)}
                          className={`p-2 rounded-lg transition-colors ${
                            city.isActive 
                              ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                          }`}
                          title={city.isActive ? 'Mark as Inactive' : 'Mark as Active'}
                        >
                          {city.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteCity(city.cityID)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete City"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Scroll indicator */}
            {filteredCities.length > 8 && (
              <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            )}
          </div>

          {(!filteredCities || filteredCities.length === 0) && !loading && (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(!cities || cities.length === 0) ? 'No cities in database' : 'No cities match your filters'}
              </h3>
              <p className="text-gray-500">
                {(!cities || cities.length === 0)
                  ? 'Add your first city to get started.' 
                  : 'Try adjusting your search criteria or clear the filters to see all cities.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* City Modal */}
      <CityModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCity(null);
        }}
        onSave={handleSaveCity}
        city={editingCity}
      />
    </div>
  );
};

export default CityManagement;
